import type { PointsToCashRedemptionBracket } from "@b2bportal/rbc-points-to-cash-api";
import type { CashEquivalent } from "../hooks/useRewardsSelection";

/**
 * Points -> cash conversion driven by the authoritative BE brackets returned by
 * POST /api/v0/pointsToCash/quote. Brackets arrive pre-sorted by the BE and each
 * carries its own `multiplier`; `cutoff` is a CUMULATIVE cash ceiling
 * (bracket[i].cutoff = bracket[i-1].cutoff + that product's flex-eligible cash).
 *
 * We consume the response as-is: no local rate constants, no business-class hack.
 * The cents-safe folding mirrors `calculateCashEquivalent` (RoundingMode.DOWN,
 * per-bracket floor then sum) so FE and BE agree to the cent.
 */

// 1e9/1e7 = 1e2: cents conversion folded into the denominator to avoid
// MAX_SAFE_INTEGER overflow. Matches calculateCashEquivalent exactly.
const pointsToCents = (points: number, multiplier: number): number =>
  Math.floor(Math.round(points * multiplier * 1e9) / 1e7);

/**
 * Convert `points` to their cash equivalent by walking brackets in order,
 * consuming up to `bracket.points` at each `bracket.multiplier`. The result is
 * clamped to the final cumulative cutoff so it can never exceed the BE ceiling.
 */
export const pointsToCashFromBrackets = (
  points: number,
  brackets: PointsToCashRedemptionBracket[] | undefined,
  currencyCode: string
): CashEquivalent => {
  if (
    !Number.isFinite(points) ||
    points <= 0 ||
    brackets == null ||
    brackets.length === 0
  ) {
    return { value: 0, currencyCode };
  }

  let remaining = Math.floor(points);
  let cents = 0;
  for (const bracket of brackets) {
    if (remaining <= 0) break;
    const take = Math.min(remaining, bracket.points);
    cents += pointsToCents(take, bracket.multiplier);
    remaining -= take;
  }

  const value = cents / 100;
  const cap = brackets[brackets.length - 1]?.cutoff?.value;
  return {
    value: cap != null ? Math.min(value, cap) : value,
    currencyCode,
  };
};
